import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [myCourses, setMyCourses] = useState([]);
  const [badges, setBadges] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loginStreak, setLoginStreak] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, coursesRes, badgesRes, streakRes, announcementsRes] = await Promise.all([
          api.get('/progress/stats'),
          api.get('/enrollments/my-courses'),
          api.get('/badges/my-badges'),
          api.get('/auth/login-streak').catch(() => ({ data: { streak: 0 } })),
          api.get('/announcements/my-announcements').catch(() => ({ data: [] })),
        ]);

        setStats(statsRes.data);
        setMyCourses(coursesRes.data.slice(0, 3));
        setBadges(badgesRes.data.slice(0, 4));
        setLoginStreak(streakRes.data.streak || 0);
        setAnnouncements(Array.isArray(announcementsRes.data) ? announcementsRes.data.slice(0, 5) : []);

        const weeklyActivity = statsRes.data?.weekly_activity;
        setRecentActivity(Array.isArray(weeklyActivity) ? weeklyActivity : []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getCompletionPercentage = () => {
    const enrolled = parseInt(stats?.enrolled_courses) || 0;
    const completed = parseInt(stats?.completed_courses) || 0;
    if (enrolled === 0) return 0;
    return Math.round((completed / enrolled) * 100);
  };

  const getCategoryProgress = () => {
    if (!myCourses || myCourses.length === 0) return [];

    const categories = {};
    myCourses.forEach((course) => {
      const cat = course.category_name || 'Other';
      if (!categories[cat]) {
        categories[cat] = { total: 0, completed: 0 };
      }
      categories[cat].total += parseInt(course.total_videos) || 0;
      categories[cat].completed += parseInt(course.completed_videos) || 0;
    });

    return Object.entries(categories)
      .map(([name, data]) => ({
        name,
        percentage:
          data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
      }))
      .sort((a, b) => b.percentage - a.percentage);
  };

  const getBadgeEmoji = (name) => {
    const emojiMap = {
      'First Steps': '👣',
      'Quick Learner': '🚀',
      'Course Champion': '🏆',
      'Quiz Master': '🎯',
      'Dedicated Student': '🔥',
      'Scholar': '🎓',
    };
    return emojiMap[name] || '⭐';
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const completionPercentage = getCompletionPercentage();
  const categoryProgress = getCategoryProgress();
  const totalWatchMinutes = Math.floor((parseInt(stats?.total_watch_time) || 0) / 60);
  const watchDisplay =
    totalWatchMinutes >= 60
      ? `${Math.round(totalWatchMinutes / 60)}h`
      : `${totalWatchMinutes || 0}m`;
  const nextCourse = myCourses[0];
  const nextCourseProgress = nextCourse
    ? (() => {
        const total = parseInt(nextCourse.total_videos) || 0;
        const completed = parseInt(nextCourse.completed_videos) || 0;
        return total > 0 ? Math.round((completed / total) * 100) : 0;
      })()
    : 0;
  const topCategory = categoryProgress[0];
  const enrolledCount = parseInt(stats?.enrolled_courses) || 0;
  const completedCount = parseInt(stats?.completed_courses) || 0;
  const inProgressCourses = Math.max(enrolledCount - completedCount, 0);

  return (
    <div className="page dashboard-page">
      <div className="container">
        <section className="dashboard-hero">
          <div className="hero-content">
            <p className="eyebrow">Learning workspace</p>
            <h1>Welcome back, {user.first_name}!</h1>
            <p className="hero-text">
              {stats?.enrolled_courses
                ? `You're tracking ${stats.enrolled_courses} course${
                    stats.enrolled_courses === 1 ? '' : 's'
                  } with ${stats.completed_courses || 0} completed so far.`
                : 'Enroll in your first course to unlock tailored insights.'}
            </p>

            <div className="hero-actions">
              {nextCourse ? (
                <Link
                  to={`/courses/${nextCourse.course_id}`}
                  className="btn-primary"
                >
                  Resume "{nextCourse.title}"
                </Link>
              ) : (
                <Link to="/courses" className="btn-primary">
                  Browse courses
                </Link>
              )}
              <Link to="/goals" className="btn-ghost">
                Review goals
              </Link>
            </div>

            <div className="hero-metrics">
              <div className="metric">
                <span className="metric-label">Active streak</span>
                <span className="metric-value">{loginStreak || 0} days</span>
              </div>
              <div className="metric">
                <span className="metric-label">Watch time</span>
                <span className="metric-value">{watchDisplay}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Badges earned</span>
                <span className="metric-value">{badges.length}</span>
              </div>
            </div>
          </div>

          <div className="hero-progress">
            <div className="hero-progress-card">
              <div className="hero-progress-header">
                <span>Overall completion</span>
                <span className="hero-progress-value">{completionPercentage}%</span>
              </div>
              <div className="progress-ring hero-ring">
                <svg viewBox="0 0 36 36">
                  <path
                    className="ring-bg"
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="ring-progress"
                    strokeDasharray={`${completionPercentage}, 100`}
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="ring-text">
                  <span className="percentage">{completionPercentage}%</span>
                  <span className="label">Complete</span>
                </div>
              </div>
              <div className="hero-progress-meta">
                <div>
                  <span>Completed</span>
                  <strong>{stats?.completed_courses || 0}</strong>
                </div>
                <div>
                  <span>In progress</span>
                  <strong>{inProgressCourses}</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📚</div>
            <div>
              <p className="stat-label">Enrolled courses</p>
              <p className="stat-value">{stats?.enrolled_courses || 0}</p>
              <span className="stat-subtext">{inProgressCourses} in progress</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div>
              <p className="stat-label">Completed</p>
              <p className="stat-value">{stats?.completed_courses || 0}</p>
              <span className="stat-subtext">Lifetime achievements</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎬</div>
            <div>
              <p className="stat-label">Videos watched</p>
              <p className="stat-value">{stats?.videos_completed || 0}</p>
              <span className="stat-subtext">Across all courses</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏱️</div>
            <div>
              <p className="stat-label">Watch time</p>
              <p className="stat-value">{totalWatchMinutes || 0} min</p>
              <span className="stat-subtext">Logged on platform</span>
            </div>
          </div>
        </section>

        <section className="insights-grid">
          <div className="insight-card">
            <div className="insight-header">
              <h3>Weekly activity</h3>
              <span className="insight-caption">
                {recentActivity.length > 0 ? 'Last 7 days' : 'Tracking starts after your next session'}
              </span>
            </div>
            {recentActivity.length > 0 ? (
              <div className="bar-chart">
                {recentActivity.map((day, index) => (
                  <div key={index} className="bar-item">
                    <div className="bar-container">
                      <div
                        className="bar"
                        style={{ height: `${Math.min(day.hours * 30, 100)}%` }}
                      ></div>
                    </div>
                    <span className="bar-label">{day.day}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="insight-placeholder">
                <p>No weekly activity yet.</p>
                <span>Complete a lesson to see how your study time trends.</span>
              </div>
            )}
          </div>

          <div className="insight-card focus-card">
            <div className="insight-header">
              <h3>Learning focus</h3>
              <span className="insight-caption">Quick snapshot</span>
            </div>
            <div className="focus-list">
              <div className="focus-row">
                <div>
                  <p className="label">Top category</p>
                  <p className="value">
                    {topCategory
                      ? `${topCategory.name}`
                      : 'Pick a track to unlock insights'}
                  </p>
                </div>
                <span className="focus-detail">
                  {topCategory ? `${topCategory.percentage}% complete` : 'No data yet'}
                </span>
              </div>
              <div className="focus-row">
                <div>
                  <p className="label">Active courses</p>
                  <p className="value">{inProgressCourses}</p>
                </div>
                <span className="focus-detail">In progress right now</span>
              </div>
              <div className="focus-row">
                <div>
                  <p className="label">Badges earned</p>
                  <p className="value">{badges.length}</p>
                </div>
                <span className="focus-detail">Keep up the streak</span>
              </div>
            </div>
            {nextCourse && (
              <div className="focus-next">
                <div>
                  <p className="label">Next up</p>
                  <h4>{nextCourse.title}</h4>
                  <p className="value">{nextCourseProgress}% complete</p>
                </div>
                <Link
                  to={`/courses/${nextCourse.course_id}`}
                  className="course-link"
                >
                  Resume
                </Link>
              </div>
            )}
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <h3>Progress by category</h3>
              <span className="insight-caption">Where you spend time</span>
            </div>
            <div className="category-bars">
              {categoryProgress.length > 0 ? (
                categoryProgress.map((cat, index) => (
                  <div key={index} className="category-item">
                    <div className="category-header">
                      <span className="category-name">{cat.name}</span>
                      <span className="category-percent">{cat.percentage}%</span>
                    </div>
                    <div className="category-bar">
                      <div
                        className="category-fill"
                        style={{ width: `${cat.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-data">Enroll in courses to see progress</p>
              )}
            </div>
          </div>
        </section>

        {badges.length > 0 && (
          <section className="dashboard-section">
            <div className="section-header">
              <div>
                <p className="eyebrow">Motivation</p>
                <h2>Recent badges</h2>
              </div>
              <Link to="/badges" className="view-all-link">
                View all
              </Link>
            </div>
            <div className="badges-preview">
              {badges.map((badge) => (
                <div key={badge.id} className="badge-preview-item">
                  <span className="badge-emoji">{getBadgeEmoji(badge.name)}</span>
                  <div>
                    <p className="badge-name">{badge.name}</p>
                    <p className="badge-meta">{badge.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {announcements.length > 0 && (
          <section className="dashboard-section">
            <div className="section-header">
              <div>
                <p className="eyebrow">From your courses</p>
                <h2>
                  Announcements
                  {announcements.filter((a) => !a.is_read).length > 0 && (
                    <span className="unread-badge">
                      {announcements.filter((a) => !a.is_read).length} new
                    </span>
                  )}
                </h2>
              </div>
              <Link to="/announcements" className="view-all-link">
                View all
              </Link>
            </div>
            <div className="announcements-widget">
              {announcements.map((a) => (
                <Link
                  key={a.id}
                  to={a.course_id ? `/courses/${a.course_id}/announcements` : '/announcements'}
                  className={`announcement-widget-item ${!a.is_read ? 'unread' : ''} ${a.announcement_type}`}
                >
                  <div className="awi-left">
                    {!a.is_read && <span className="awi-dot" />}
                    <div className="awi-text">
                      <p className="awi-title">{a.title}</p>
                      {a.course_title && (
                        <p className="awi-course">{a.course_title}</p>
                      )}
                    </div>
                  </div>
                  <div className="awi-right">
                    {a.link_url && (
                      <a
                        href={a.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="awi-join-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Join
                      </a>
                    )}
                    <span className="awi-date">
                      {new Date(a.published_at).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="dashboard-section">
          <div className="section-header">
            <div>
              <p className="eyebrow">Continue</p>
              <h2>Learning queue</h2>
            </div>
            <Link to="/my-courses" className="view-all-link">
              View all
            </Link>
          </div>

          {myCourses.length === 0 ? (
            <div className="empty-state">
              <p>You haven't enrolled in any courses yet.</p>
              <Link to="/courses" className="btn-primary">
                Browse courses
              </Link>
            </div>
          ) : (
            <div className="queue-grid">
              {myCourses.map((course) => {
                const totalVideos = parseInt(course.total_videos) || 0;
                const completedVideos = parseInt(course.completed_videos) || 0;
                const progress =
                  totalVideos > 0
                    ? Math.round((completedVideos / totalVideos) * 100)
                    : 0;

                return (
                  <article key={course.id} className="queue-card">
                    <div className="queue-header">
                      <div className="queue-avatar">
                        {getCourseInitials(course.title)}
                      </div>
                      <div>
                        <p className="queue-category">
                          {course.category_name || 'Course'}
                        </p>
                        <h3>{course.title}</h3>
                      </div>
                      {course.completed_at && (
                        <span className="queue-status">Completed</span>
                      )}
                    </div>
                    <p className="queue-instructor">{course.instructor_name}</p>
                    <div className="queue-progress">
                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <span className="queue-progress-value">{progress}%</span>
                    </div>
                    <p className="queue-meta">
                      {completedVideos} of {totalVideos} lessons
                      completed
                    </p>
                    <div className="queue-actions">
                      <Link
                        to={`/courses/${course.course_id}`}
                        className="course-link"
                      >
                        {course.completed_at ? 'Review course' : 'Continue learning'}
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="quick-actions">
          <Link to="/courses" className="action-card">
            <div>
              <p className="label">Explore catalog</p>
              <h4>Find a new course</h4>
            </div>
            <span className="action-icon">🔍</span>
          </Link>
          <Link to="/certificates" className="action-card">
            <div>
              <p className="label">Certifications</p>
              <h4>Download credentials</h4>
            </div>
            <span className="action-icon">🎓</span>
          </Link>
          <Link to="/badges" className="action-card">
            <div>
              <p className="label">Gamification</p>
              <h4>Review badges</h4>
            </div>
            <span className="action-icon">🏆</span>
          </Link>
        </section>
      </div>
    </div>
  );
};

const getCourseInitials = (value = '') =>
  value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

export default Dashboard;
